import React, {forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {useConfigurations, useCustomerMgr} from 'coreHooks';

import {clearScheduledDate, persistScheduledDate, toISOWithoutMs} from '../../constants';

import {IStorefrontToolkitRef, IStorefrontToolkitSectionProps} from './IStorefrontToolkitSection';
import messages from './messages';

import styles from './StorefrontToolkitSection.module.scss';

const StorefrontToolkitSection = forwardRef<IStorefrontToolkitRef, IStorefrontToolkitSectionProps>(
	({selectedDate, onToolkitSubmit, renderCustomerGroupSelector}, ref) => {
		const intl = useIntl();
		const configurationsMgr = useConfigurations();
		const CustomerMgr = useCustomerMgr();
		const customer = CustomerMgr.getCurrentCustomer();
		const dropdownContainer = useRef<HTMLDivElement>(null);
		const isCustomerReady = customer.isReady();
		const [isCollapsed, setIsCollapsed] = useState(false);

		const customerGroups = useMemo(
			() => configurationsMgr.get('customerGroups')?.map((g: string) => ({value: g, label: g})) ?? [],
			[configurationsMgr],
		);

		const activeCustomerGroups = useMemo(
			() => isCustomerReady ? customer.getCustomerGroups()?.filter((c: any) => c.isRuleBased) ?? [] : [],
			[isCustomerReady],
		);

		const [selectedCustomerGroup, setCustomerGroup] = useState<string | null>(
			isCustomerReady
				? (customer?.context?.customQualifiers as {customergroupId?: string})?.customergroupId ?? null
				: null,
		);
		const [sourceCode, setSourceCode] = useState<string | null>(
			isCustomerReady ? (customer?.context as {sourceCode?: string})?.sourceCode ?? null : null,
		);
		const [submittedCustomerGroup, setSubmittedCustomerGroup] = useState(selectedCustomerGroup);

		const buildPayload = useCallback(() => {
			const dateTime = selectedDate ? toISOWithoutMs(selectedDate) : null;

			if (selectedDate) {
				persistScheduledDate(new Date(toISOWithoutMs(selectedDate)));
			} else {
				clearScheduledDate();
			}

			return {
				groupId: selectedCustomerGroup ?? null,
				sourceCode: sourceCode ?? null,
				dateTime,
			};
		}, [selectedDate, selectedCustomerGroup, sourceCode]);

		useImperativeHandle(ref, () => ({
			getCurrentPayload: () => ({
				groupId: selectedCustomerGroup ?? null,
				sourceCode: sourceCode ?? null,
			}),
			reset: () => {
				setCustomerGroup(null);
				setSourceCode(null);
				setSubmittedCustomerGroup(null);
			},
		}), [selectedCustomerGroup, sourceCode]);

		const handleApplyStorefront = useCallback(() => {
			onToolkitSubmit(buildPayload());
		}, [buildPayload, onToolkitSubmit]);

		const handleApplyGroups = useCallback(() => {
			setSubmittedCustomerGroup(selectedCustomerGroup);
			onToolkitSubmit(buildPayload());
		}, [selectedCustomerGroup, buildPayload, onToolkitSubmit]);

		const handleClear = useCallback(() => {
			setCustomerGroup(null);
			setSubmittedCustomerGroup(null);
		}, []);

		if (!isCustomerReady) {
			return null;
		}

		const hasGroupMismatch = submittedCustomerGroup
			&& !activeCustomerGroups.some((c: any) => c.ID === submittedCustomerGroup);

		return (
			<div className={styles['storefront-section']}>
				<div
					className={styles['section-head']}
					onClick={() => setIsCollapsed((prev) => !prev)}
				>
					<h4 className={styles['section-title']}>
						{intl.formatMessage(messages.storefrontToolkitTitle)}
					</h4>
					<span
						className={cn(styles['section-chev'], {
							[styles['section-chev--collapsed']]: isCollapsed,
						})}
					>
					&#9652;
					</span>
				</div>

				<div
					className={cn(styles['section-body'], {
						[styles['section-body--collapsed']]: isCollapsed,
					})}
				>
					<div className={styles['element']}>
						<label className={styles['subsection-title']}>
							{intl.formatMessage(messages.sourceCodePlaceholder)}
						</label>
						<input
							type="text"
							placeholder={intl.formatMessage(messages.sourceCodePlaceholder)}
							value={sourceCode ?? ''}
							onChange={(e) => setSourceCode(e.target.value)}
						/>
						<button className={styles['submit-button']} onClick={handleApplyStorefront}>
							{intl.formatMessage(messages.buttonApplyStorefrontLabel)}
						</button>
					</div>

					<div className={styles['groups-card']}>
						<div className={styles['groups-card-header']}>
							<label className={styles['groups-card-title']}>
								{intl.formatMessage(messages.customerGroupsTitle)}
							</label>
							<span className={styles['groups-card-count']}>
								{activeCustomerGroups.length}/{customerGroups.length}
							</span>
						</div>

						{renderCustomerGroupSelector && (
							<div ref={dropdownContainer} className={styles['element']}>
								{renderCustomerGroupSelector({
									value: selectedCustomerGroup,
									options: customerGroups,
									onSelect: setCustomerGroup,
									containerRef: dropdownContainer,
								})}
							</div>
						)}

						<div className={styles['element']}>
							<p className={styles['active-groups-title']}>
								{intl.formatMessage(messages.activeGroupsTitle, {count: activeCustomerGroups.length})}
							</p>
							<ul className={styles['active-groups-list']}>
								{activeCustomerGroups.map((customerGroup: any) => (
									<li key={`ac-${customerGroup.ID}`}>
										{customerGroup.ID === submittedCustomerGroup ? (
											<strong className={styles['highlight']}>{customerGroup.ID}</strong>
										) : (
											<span>{customerGroup.ID}</span>
										)}
									</li>
								))}
							</ul>
						</div>

						<div className={styles['button-row']}>
							<button className={styles['reset-button']} onClick={handleClear}>
								{intl.formatMessage(messages.buttonClearLabel)}
							</button>
							<button className={styles['submit-button']} onClick={handleApplyGroups}>
								{intl.formatMessage(messages.buttonApplyGroupsLabel)}
							</button>
						</div>

						{hasGroupMismatch && (
							<p className={styles['error']}>
								{intl.formatMessage(messages.customerGroupConfigurationError)}
							</p>
						)}
					</div>
				</div>
			</div>
		);
	},
);

StorefrontToolkitSection.displayName = 'StorefrontToolkitSection';

export default StorefrontToolkitSection;
